// API Configuration
// This file contains the base URL and other API-related configurations

/**
 * Helper function to get environment variables from window._env_
 * Falls back to process.env for local development
 * @param {string} key - Environment variable key
 * @param {string} defaultValue - Default value if not found
 * @returns {string} Environment variable value
 */
const getEnvValue = (key, defaultValue = "") => {
  // First check window._env_ (runtime config from public/config.js)
  if (window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  // Fallback to process.env for local development
  if (process.env[key]) {
    return process.env[key];
  }
  return defaultValue;
};

// Base API URL
export const API_BASE_URL = getEnvValue(
  "REACT_APP_API_URL",
  "https://orbit-connectors-74624883720.us-central1.run.app"
);

/** Base URL for Ask Orbit SSE chat (`/chat`, `/healthz`) — same host as API by default */
export const ORBIT_CHAT_API_BASE = getEnvValue(
  "REACT_APP_ORBIT_CHAT_API_BASE",
  `${String(API_BASE_URL).replace(/\/$/, "")}/api/v1`
).replace(/\/$/, "");

// Application Version
export const APP_VERSION = getEnvValue("REACT_APP_VERSION", "0.1.0");

// Google SSO Client ID
export const GOOGLE_CLIENT_ID = getEnvValue(
  "REACT_APP_GOOGLE_CLIENT_ID",
  "74624883720-ab19vi1u06pj7ugnko7cm5fjvjd5h7q4.apps.googleusercontent.com"
);

// Microsoft SSO Client ID
export const MICROSOFT_CLIENT_ID = getEnvValue(
  "REACT_APP_MICROSOFT_CLIENT_ID",
  "98626e0a-32cc-4546-9899-8e4e814c1841"
);

/**
 * Origins allowed to hand Orbit an id_token via postMessage (the LexAI "Orbit"
 * tab). Comma-separated, set per environment via REACT_APP_HANDOFF_ALLOWED_ORIGINS.
 *
 * Defaults to the known LexAI tenant portals. Setting the env var REPLACES this
 * list rather than adding to it, so any environment that overrides it must
 * enumerate every origin it wants to trust.
 *
 * This list is the only thing standing between the handoff and any page on the
 * internet posting a credential at us, so it must stay an exact-origin
 * allowlist — never a suffix or wildcard match.
 *
 * Entries must be full origins including the scheme, since they are compared
 * against event.origin and passed to postMessage as targetOrigin. A bare
 * hostname matches nothing and makes postMessage throw. Trailing slashes are
 * stripped below, but a path or port would have to match exactly.
 */
export const HANDOFF_ALLOWED_ORIGINS = getEnvValue(
  "REACT_APP_HANDOFF_ALLOWED_ORIGINS",
  [
    "https://moe.lexai.nativeorange.ai",
    "https://al.app.lexai.nativeorange.ai",
    "https://granwest.app.lexai.nativeorange.ai"
  ].join(",")
)
  .split(",")
  .map((o) => o.trim().replace(/\/$/, ""))
  .filter(Boolean);

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  SSO_LOGIN: `${API_BASE_URL}/api/v1/auth/login/sso`,
  PASSWORD_LOGIN: `${API_BASE_URL}/api/v1/auth/login/password`,
  PASSWORD_SETUP_VALIDATE: `${API_BASE_URL}/api/v1/auth/password-setup/validate`,
  PASSWORD_SETUP: `${API_BASE_URL}/api/v1/auth/password-setup`,

  // Providers
  PROVIDER_CATEGORIES: `${API_BASE_URL}/api/v1/providers/categories`,
  PROVIDER_AUTH_TYPES: `${API_BASE_URL}/api/v1/providers/auth-types`,
  PROVIDERS: `${API_BASE_URL}/api/v1/metrics/providers`, // Provider metrics endpoint
  PROVIDER_APIS: `${API_BASE_URL}/api/v1/metrics/providers`, // Provider API details endpoint

  // Users
  USERS: `${API_BASE_URL}/api/v1/users`,

  // Organizations
  ORGANIZATIONS: `${API_BASE_URL}/api/v1/organizations`,
  ORGANIZATION_LOGO: `${API_BASE_URL}/api/v1/organizations/logo`,

  // API Keys
  API_KEYS: `${API_BASE_URL}/api/v1/api-keys`,

  // The Orbit User Guide: a guide-only link (signed in) that opens the guide's page in a new tab
  USER_GUIDE_LINK: `${API_BASE_URL}/api/v1/user-guide/link`
};

// Default export
const config = {
  API_BASE_URL,
  ORBIT_CHAT_API_BASE,
  API_ENDPOINTS,
  APP_VERSION,
  GOOGLE_CLIENT_ID,
  MICROSOFT_CLIENT_ID
};

export default config;
